import { createPortal } from "react-dom";
import { useRegisterSlotCanvas } from "@/components/ai-elements/slot-canvas";
import { useArtifact } from "@/hooks/useArtifacts";
import { useSlotCanvasStore } from "@/store/slotCanvasStore";
import { ArtifactCanvasBody } from "./ArtifactCanvasBody";

/**
 * The canvas is one slot per artifact version, because a card names the version
 * it was posted for: the card for v1 opens v1 even after the agent has
 * rewritten the page, rather than every card in the thread opening whatever
 * the artifact has since become.
 */
export const artifactSlotId = (artifactId: string, version?: number | null) =>
  version ? `artifact-${artifactId}-v${version}` : `artifact-${artifactId}`;

/** Navigator label. The version keeps two cards for one artifact apart. */
const slotTitle = (title: string, version?: number | null) =>
  version ? `${title} (v${version})` : title;

export interface ArtifactCanvasSlotProps {
  artifactId: string;
  /** The card's title, naming the slot before the artifact itself is read. */
  title: string;
  /**
   * Version this card was posted for. The panel shows that version, so an
   * earlier card keeps showing the page as it announced it.
   */
  version?: number | null;
  /**
   * Whether to show the panel as soon as the slot mounts. Off for a slot
   * mounted so something else can open it — a task's attachments, which must
   * be reachable without throwing a panel over the task.
   */
  autoOpen?: boolean;
}

/**
 * Registers one artifact as a canvas slot, and opens it when it is new.
 *
 * Auto-open is the canvas default and is one-shot per slot id, suppressed
 * while a thread is replaying its history. So a page the agent has just
 * authored shows itself, while reopening that conversation later registers its
 * cards without throwing a panel over whatever the person was reading.
 */
export function ArtifactCanvasSlot({
  artifactId,
  title,
  version,
  autoOpen = true,
}: ArtifactCanvasSlotProps) {
  const bodyMount = useSlotCanvasStore((state) => state.bodyMountElement);

  const { isOpen } = useRegisterSlotCanvas({
    id: artifactSlotId(artifactId, version),
    experience: "artifact",
    // The card's own title: the card is a receipt for what it announced, and
    // the panel header shows the artifact's current title.
    title: slotTitle(title, version),
    autoOpen,
    hasInStreamCard: true,
  });

  // Read while this panel is the open one. A conversation reloaded with a
  // dozen cards registers a dozen slots, and every read mints two presigned
  // URLs, so reading for a card nobody opened is work for nothing. Opening it
  // again reads again, which is also what keeps those URLs inside the hour
  // they are signed for.
  const {
    data: artifact,
    isLoading,
    isError,
  } = useArtifact(artifactId, version, { enabled: isOpen });

  if (!isOpen || !bodyMount) {
    return null;
  }

  return createPortal(
    <ArtifactCanvasBody
      title={artifact?.title ?? title}
      contentSrc={artifact?.url}
      downloadUrl={artifact?.download_url}
      version={artifact?.version}
      latestVersion={artifact?.latest_version}
      isPreparing={isLoading}
      isMissing={isError}
    />,
    bodyMount,
  );
}
